import { OwnerData } from "@repo/api/schemas/policiesInventory.schema";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

export interface OwnersRow extends OwnerData {
  id: string;
}

interface SelectedRowsContextType {
  selectedRows: OwnersRow[];
  setSelectedRows: (rows: OwnerData[]) => void;
  removeSelectedRow: (userkey: string) => void;
}

const SelectedRowsContext = createContext<SelectedRowsContextType | undefined>(
  undefined,
);

export function SelectedRowsProvider({
  children,
  initialValue = [],
}: {
  children: ReactNode;
  initialValue?: OwnerData[];
}) {
  const [selectedRows, setSelectedRowsInternal] = useState<OwnersRow[]>([]);

  const setSelectedRows = (rows: OwnerData[]) => {
    const dataWithId = rows.map((item) => ({ ...item, id: item.userkey }));
    setSelectedRowsInternal(dataWithId);
  };

  useEffect(() => {
    if (initialValue.length > 0) {
      setSelectedRows(initialValue);
    }
  }, [initialValue]);

  const removeSelectedRow = (userkey: string) => {
    setSelectedRowsInternal((prev) =>
      prev.filter((row) => row.userkey !== userkey),
    );
  };

  return (
    <SelectedRowsContext.Provider
      value={{ selectedRows, setSelectedRows, removeSelectedRow }}
    >
      {children}
    </SelectedRowsContext.Provider>
  );
}

export function useSelectedRows() {
  const context = useContext(SelectedRowsContext);
  if (context === undefined) {
    throw new Error(
      "useSelectedRows must be used within a SelectedRowsProvider",
    );
  }
  return context;
}
